import React, { useState, useEffect, useContext, useLayoutEffect } from "react";
import "../style/odds.css";
import axios from "axios";
import Spinner from "./Spinner";
import { AuthContext } from "../context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { io } from "socket.io-client";
import ReplayIcon from "@mui/icons-material/Replay";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import api_url from "../constants";

const ENDPOINT =
  process.env.NODE_ENV === "development"
    ? "http://localhost:9003"
    : "https://api.bsf5050.com";

const months = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function AllOdds() {
  const [authContext, setAuthContext] = useContext(AuthContext);
  const config = {
    headers: { Authorization: `Bearer ${authContext.token}` },
  };
  const [isRefreshing, setRefreshing] = useState(false);
  const [password, setPassword] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [matchOdds, setMatchOdds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchList, setMatchList] = useState([]);
  const [unresolvedMatches, setUnresolvedMatches] = useState([]);
  const [resultLoading, setResultLoading] = useState("");
  const [matchLoading, setMatchLoading] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);

  // --- Filter States ---
  const [filterMatchId, setFilterMatchId] = useState("");
  const [filterType, setFilterType] = useState("All"); // All, Match Bets, Session Bets

  // --- Toggle States ---
  const [showSessionSection, setShowSessionSection] = useState(true);
  
  // visibleSessions tracks which are OPEN. Default is closed (empty).
  const [visibleSessions, setVisibleSessions] = useState({});

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleSession = (matchId) => {
    setVisibleSessions((prev) => ({
      ...prev,
      [matchId]: !prev[matchId],
    }));
  };

  const settleMatch = async (matchId) => {
    const winnerSid = document.getElementById(matchId).value;
    setMatchLoading(matchId);

    if (winnerSid) {
      const response = await axios.post(
        `${api_url}/bet/settleMatchBet`,
        { matchId, winnerSid },
        config
      );
      toast.success(response.data.msg);
    }
    setMatchLoading("");
  };

  const deleteBet = async (fancyName, matchId, password) => {
    setResultLoading(fancyName);
    try {
      const response = await axios.post(
        `${api_url}/bet/deleteFancyBet`,
        { fancyName, matchId, password },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authContext.token}`,
          },
        }
      );
      toast.success("Bet deleted successfully!");
      setResultLoading("");
      setPassword("");
      setShowModal(false);
      setPendingDelete(null);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to delete bet");
      setResultLoading("");
      setPassword("");
      setShowModal(false);
      setPendingDelete(null);
    }
  };

  const handleDeleteClick = (fancyName, matchId) => {
    setPendingDelete({ fancyName, matchId });
    setShowModal(true);
  };

  const handlePasswordSubmit = () => {
    if (!password.trim()) {
      toast.error("Please enter password");
      return;
    }
    if (pendingDelete) {
      deleteBet(pendingDelete.fancyName, pendingDelete.matchId, password);
    }
  };

  const handleModalCancel = () => {
    setPassword("");
    setShowModal(false);
    setPendingDelete(null);
  };

  const getSessionOdds = async (socket) => {
    try {
      socket.on("allBets", (data) => {
        if (data) {
          setMatchOdds(data);
        }
      });
    } catch (error) {
      console.log(error);
    }
    socket.on("error", (message) => {
      setMatchOdds([]);
    });
    setLoading(false);
  };

  const getUnsettledMatch = async (socket) => {
    try {
      socket.on("unsettledMatches", (data) => {
        if (data) {
          setUnresolvedMatches(data);
        }
      });
    } catch (error) {
      console.log(error);
    }
    socket.on("error", (message) => {
      console.log(message);
      setMatchOdds({});
    });
    socket.emit("getUnsettledMatch");
    setLoading(false);
  };

  async function restartServer() {
    try {
      setPassword("");
      setShowModal(false);
      const response = await axios.post(
        `${api_url}/api/restartServer`,
        { password },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authContext.token}`,
          },
        }
      );
      if (response.data.msg) {
        toast.error(response.data.msg);
        setShowModal(false);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function getMatchData() {
    try {
      const response = await axios.get(`${api_url}/sports/getMatchList`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authContext.token}`,
        },
      });
      setMatchList(response.data);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    getMatchData();
  }, []);

  useEffect(() => {
    const socket = io(ENDPOINT, {
      path: "/socket/bets",
      transports: ["websocket"],
    });
    getSessionOdds(socket);
    getUnsettledMatch(socket);
    socket.emit("getAllBets");
    socket.emit("getUnsettledMatch");

    const interval = setInterval(() => {
      socket.emit("getAllBets");
    }, 1000);
    const interval2 = setInterval(() => {
      socket.emit("getUnsettledMatch");
    }, 3000);
    return () => {
      socket.disconnect();
      clearInterval(interval);
      clearInterval(interval2);
    };
  }, []);

  // --- Filter Logic ---
  const shouldShowMatchBets = filterType === "All" || filterType === "Match Bets";
  const shouldShowSessionBets = filterType === "All" || filterType === "Session Bets";

  const filteredUnresolvedMatches = unresolvedMatches.filter((match) => {
    return filterMatchId === "" || match.event_id === filterMatchId;
  });

  const filteredMatchList = matchList.filter((match) => {
    return filterMatchId === "" || match.event_id === filterMatchId;
  });

  if (loading) {
    return (
      <div className="body-content no-padding">
        <Spinner color="#555" />
      </div>
    );
  }

  return (
    <>
      <style>
        {`
        .filter-container {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
          margin: 15px 10px;
        }
        .filter-select {
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 15px;
          color: #333;
          outline: none;
          cursor: pointer;
          min-width: 200px;
          transition: 0.2s ease;
        }
        .filter-select:hover, .filter-select:focus {
          border-color: #007bff;
          box-shadow: 0 0 5px rgba(0,123,255,0.3);
        }
        @media (max-width: 600px) {
          .filter-select {
            width: 100%;
          }
        }
        `}
      </style>

      {showModal ? (
        <div className="modal-body">
          <div className="modal-content">
            <div className="modal-header">Enter Password to Delete</div>
            <div className="modal-c" style={{ backgroundColor: "#fff" }}>
              <div>
                <input
                  autoComplete="off"
                  type="password"
                  placeholder="Password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handlePasswordSubmit();
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-btn">
              <button
                className="warn-btn"
                onClick={handlePasswordSubmit}
                disabled={resultLoading !== ""}
              >
                {resultLoading !== "" ? <Spinner color="#fff" /> : "Delete"}
              </button>
              <button onClick={handleModalCancel}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="body-content no-padding">
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />

        <div style={{ padding: "10px 10px 0 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button className="warn-btn" onClick={() => setShowModal(true)}>
            Restart Server
          </button>
        </div>

        {/* --- FILTERS --- */}
        <div className="filter-container">
          <select
            className="filter-select"
            value={filterMatchId}
            onChange={(e) => setFilterMatchId(e.target.value)}
          >
            <option value="">All Matches</option>
            {unresolvedMatches.map((match) => (
              <option key={match.event_id} value={match.event_id}>
                {match.event_name}
              </option>
            ))}
          </select>

          <select
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            {/* <option value="All">All Bet Types</option> */}
            <option value="Session Bets">Session Bets (Fancy)</option>
          </select>

          <span
            onClick={() => {
              setRefreshing(true);
              window.location.reload();
            }}
            className={isRefreshing ? "rotating" : ""}
            style={{ cursor: "pointer", marginLeft: "auto", color: "#555" }}
            title="Refresh Data"
          >
            <ReplayIcon style={{ fontSize: 30 }} />
          </span>
        </div>

        
        {/* --- SECTION 2: SESSION BETS (Odds Tables) --- */}
        {shouldShowSessionBets && (
          <>
            <h3
              style={{
                padding: "20px 10px 10px 10px",
                color: "#555",
                borderBottom: "1px solid #eee",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none"
              }}
              onClick={() => setShowSessionSection(!showSessionSection)}
            >
              <span>Session Bets (Fancy)</span>
              <span>
                {showSessionSection ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </span>
            </h3>

            {showSessionSection && filteredMatchList.map((match) => {
              const matchName = match.event_name;
              const data = matchOdds.filter((x) => x.match_id === match.event_id);
              const isVisible = visibleSessions[match.event_id]; // Default closed
              
              if (data.length === 0) return null;

              return (
                <div
                  className="pr-box"
                  key={match.event_id}
                  style={{ paddingBottom: !isVisible ? 20 : 80 }}
                >
                  <div 
                    className="prbox-header"
                    onClick={() => toggleSession(match.event_id)}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      userSelect: "none"
                    }}
                  >
                    <span>{matchName}</span>
                     <span style={{ display: "flex", alignItems: "center" }}>
                        {isVisible ? <KeyboardArrowUpIcon style={{ fontSize: 20 }}/> : <KeyboardArrowDownIcon style={{ fontSize: 20 }}/>}
                    </span>
                  </div>
                  
                  {isVisible && (
                  <div className="prbox-body odds-table">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: 50 }}>S.No</th>
                          <th>SESSION</th>
                          <th colSpan={2}>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((odd, idx) => {
                          return (
                            <tr key={odd.fancyName}>
                              <td style={{ textAlign: "center", fontWeight: 600 }}>{idx + 1}</td>
                              <td>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: "100%",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {odd.fancyName}
                                  </span>
                                  <span>:{odd.runs}</span>
                                </div>
                              </td>
                              <td>
                                <div
                                  className="table-flex"
                                  style={{
                                    justifyContent: "center",
                                    alignItems: "center",
                                    display: "flex",
                                  }}
                                >
                                  <button
                                    className="warn-btn"
                                    onClick={() =>
                                      resultLoading === odd.fancyName
                                        ? () => {}
                                        : resultLoading.length === 0
                                        ? handleDeleteClick(
                                            odd.fancyName,
                                            odd.match_id
                                          )
                                        : () => {}
                                    }
                                    disabled={
                                      (resultLoading.length > 0 &&
                                        resultLoading !== odd.fancyName) ||
                                      resultLoading !== ""
                                    }
                                    style={{
                                      border: "none",
                                      padding: "8px 12px",
                                    }}
                                  >
                                    {resultLoading === odd.fancyName ? (
                                      <Spinner
                                        color="#fff"
                                        style={{
                                          height: 13,
                                          width: 13,
                                          borderWidth: 2,
                                        }}
                                      />
                                    ) : (
                                      "Delete"
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div></div>
                  </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </>
  );
}

export default AllOdds;